/* eslint-disable max-len */
import { JSONSchema7 } from 'json-schema';
import { types as commonTypes, customSchemas } from '@sitebuilder/common';
import templateView from './RootDocument';

const schema: JSONSchema7 = {
    title: 'Root Document',
    type: 'object',
    required: [],
    properties: {
        styles: {
            type: 'string',
            description: 'A stylesheet',
            default: '',
        },
        ...customSchemas.textSchema.TextSchema.schema,
        ...customSchemas.styleSchema.StyleSchema.schema,
        ...customSchemas.layoutSchema.LayoutSchema.schema,
        dataSource: {
            type: 'string',
            title: 'Data source',
            default: '',
        },
        dataType: {
            type: 'string',
            title: 'Data Type',
            enum: ['none', 'webProfile', 'fundList'],
            default: '',
        },
        includeAllProfiles: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Include all profiles',
        },
        includeHistoricalHoldings: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Include historical holdings',
        },
        includeUnderlyingFundPerformance: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Include underlying performance',
        },
        includeFundSeriesPerformance: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Include series performance',
        },
        includeAnnotations: {
            type: 'boolean',
            enum: [true, false],
            default: true,
            title: 'Include annotations',
        },
        annotationFilterProperty: {
            type: 'string',
            title: 'Annotation Filter Property',
            default: 'useForPdfProfiles',
        },
        isWithinIFrame: {
            type: 'boolean',
            enum: [true, false],
            default: false,
            title: 'Is within Iframe',
            description: 'Select whether document is placed within IFrame or not',
        },
        addScripts: {
            type: 'boolean',
            title: 'Add scripts',
            description: 'Add script tags when page is within iframe',
        },
        documentType: {
            type: 'string',
            title: 'Type of Document',
            enum: ['web', 'pdf', 'pptx'],
            default: 'web',
        },
        colorSpace: {
            type: 'string',
            title: 'Type of Color Space',
            enum: ['rgb', 'hex', 'cmyk', 'spot'],
            default: 'rgb',
        },
        pageTitleEn: {
            type: 'string',
            title: 'Add page title for EN locale (deprecated, use pageTitle instead)',
            default: '',
        },
        pageTitleFr: {
            type: 'string',
            title: 'Add page title for FR locale (deprecated, use pageTitle instead)',
            default: '',
        },
        loadingDataText: {
            type: 'string',
            title: 'Loading Data Text',
            default: '',
        },
        showData: {
            type: 'boolean',
            enum: [true, false],
            default: false,
            title: 'Show Data',
        },
        selectFunds: {
            type: 'boolean',
            enum: [true, false],
            default: false,
            title: 'Select Funds',
        },
        selectedFunds: {
            type: 'string',
            default: '',
            title: 'Selected Funds',
        },
        fonts: {
            type: 'array',
            title: 'Add Fonts',
            items: {
                type: 'object',
                properties: {
                    name: {
                        type: 'string',
                        title: 'Enter Font Name',
                    },
                    url: {
                        type: 'string',
                        title: 'Enter Font Url',
                    },
                    format: {
                        type: 'string',
                        title: 'Enter Font Format',
                    },
                },
            },
        },
        sortAnnotationsBy: {
            type: 'string',
            title: 'Sort Annoatations by Id',
        },
        annotationsMarkerHtml: {
            type: 'string',
            title: 'Annotations Marker Html',
        },
        annotationsTextHtml: {
            type: 'string',
            title: 'Annotations Text Html',
        },
        annotationsTextGroupHtml: {
            type: 'string',
            title: 'Annotations Text Group Html',
        },
        themes: {
            type: 'array',
            title: 'Add theme variables',
            items: {
                type: 'object',
                properties: {
                    id: {
                        type: 'string',
                        title: 'Enter theme ID',
                    },
                    conditionFunction: {
                        type: 'string',
                        title: 'Enter condition to apply theme',
                    },
                    themeStyles: {
                        type: 'string',
                        description:
                            'A css variable. Ensure you use --color-graph-l & --color-graph-lb when theming growthChart',
                        default: '',
                    },
                    colorPallete: {
                        type: 'array',
                        title: 'Add color pallete',
                        items: {
                            type: 'string',
                            description: 'A color',
                        },
                    },
                },
            },
        },
        // meta data
        pageTitle: {
            type: 'string',
            title: 'Page Title',
            default: '',
        },
        pageDescription: {
            type: 'string',
            title: 'Page Description',
            default: '',
        },
        keywords: {
            type: 'string',
            title: 'Keywords',
            description: 'Enter comma separated values',
        },
        pageAuthor: {
            type: 'string',
            title: 'Page Author',
        },
        canonicalURL: {
            type: 'string',
            title: 'Canonical URL',
        },
        externalCSSLink: {
            type: 'string',
            title: 'Link to external style sheet',
        },
        robots: {
            type: 'string',
            title: 'Robots',
            description: 'Enter comma separated values',
        },
        fileName: {
            type: 'string',
            title: 'File name',
            default: '',
        },
        redirect301: {
            type: 'string',
            title: '301 Redirect',
            default: '',
        },
        redirect302: {
            type: 'string',
            title: '302 Redirect',
            default: '',
        },
        cachingHeader: {
            type: 'string',
            title: 'Cache Control',
            default: '',
            pattern: '^[ A-Za-z0-9/,=-]*$',
        },
        cspHeader: {
            type: 'string',
            title: 'Content Security Policy Header',
            default: '',
        },
        theme: {
            type: 'string',
            title: 'Select Theme',
            default: 'Default',
        },
        isDynamicTheme: {
            type: 'boolean',
            title: 'Select Theme Dynamically',
            default: false,
        },
        usePageAnalytics: {
            type: 'boolean',
            title: 'Use Page Analytics',
            default: false,
        },
        disableTabStaticData: {
            type: 'boolean',
            title: 'Disable tabbing through static data',
            default: false,
        },
        profileIdTransform: {
            type: 'boolean',
            title: 'Profile ID Transform',
            default: false,
        },
        isTransformData: {
            type: 'boolean',
            title: 'Transform Data',
            default: false,
        },
        locales: {
            type: 'array',
            title: 'Add New Locale',
            items: {
                type: 'string',
            },
            default: ['en-US', 'fr-CA'],
        },
    },
    dependencies: {
        documentType: {
            oneOf: [
                {
                    properties: {
                        documentType: { enum: ['web'] },
                    },
                },
                {
                    properties: {
                        documentType: { enum: ['pdf'] },
                        pageSize: {
                            type: 'string',
                            title: 'Page Size',
                            enum: ['letter', 'A4', 'legal', 'Custom'],
                            default: 'letter',
                        },
                        pageOrientation: {
                            type: 'string',
                            title: 'Orientation',
                            enum: ['portrait', 'landscape'],
                            default: 'portrait',
                        },
                        pageMargin: {
                            type: 'object',
                            title: 'Page Margin',
                            properties: {
                                top: {
                                    title: 'Top',
                                    type: 'number',
                                    default: 0.4,
                                },
                                left: {
                                    title: 'Left',
                                    type: 'number',
                                    default: 0.4,
                                },
                                bottom: {
                                    title: 'Bottom',
                                    type: 'number',
                                    default: 0.4,
                                },
                                right: {
                                    title: 'Right',
                                    type: 'number',
                                    default: 0.4,
                                },
                            },
                        },
                        pageBleed: {
                            type: 'object',
                            title: 'Page Bleed',
                            properties: {
                                top: {
                                    title: 'Top',
                                    type: 'number',
                                    default: 0,
                                },
                                left: {
                                    title: 'Left',
                                    type: 'number',
                                    default: 0,
                                },
                                bottom: {
                                    title: 'Bottom',
                                    type: 'number',
                                    default: 0,
                                },
                                right: {
                                    title: 'Right',
                                    type: 'number',
                                    default: 0,
                                },
                            },
                        },
                        addPdfTags: {
                            type: 'boolean',
                            title: 'Add tags',
                            default: true,
                        },
                        conformance: {
                            type: 'string',
                            title: 'Conformance',
                            enum: [
                                'PDFA1A_PDFUA1',
                                'PDFA2A_PDFUA1',
                                'PDFA3A_PDFUA1',
                                'PDFUA1',
                                'Custom',
                            ],
                            default: 'PDFA3A_PDFUA1',
                        },
                        conversionEnabled: {
                            type: 'boolean',
                            title: 'Conversion Enabled',
                            default: false,
                        },
                        // pdf specific meta data
                        pageSubject: {
                            type: 'string',
                            title: 'Page Subject',
                        },
                        pageCreator: {
                            type: 'string',
                            title: 'Page Creator',
                        },
                        isRunningHeader: {
                            type: 'boolean',
                            title: 'Is Running Header',
                            default: false,
                        },
                        isRunningFooter: {
                            type: 'boolean',
                            title: 'Is Running Footer',
                            default: false,
                        },
                    },
                    dependencies: {
                        pageSize: {
                            oneOf: [
                                {
                                    properties: {
                                        pageSize: { enum: ['Custom'] },
                                        pageWidth: {
                                            title: 'Width',
                                            type: 'number',
                                            default: 8.5,
                                        },
                                        pageHeight: {
                                            title: 'Height',
                                            type: 'number',
                                            default: 11,
                                        },
                                    },
                                },
                            ],
                        },
                        conformance: {
                            oneOf: [
                                {
                                    properties: {
                                        conformance: { enum: ['Custom'] },
                                        customConformance: {
                                            title: 'Custom conformance',
                                            type: 'string',
                                        },
                                    },
                                },
                            ],
                        },
                        conversionEnabled: {
                            oneOf: [
                                {
                                    properties: {
                                        conversionEnabled: { enum: [true] },
                                        coversionProfile: {
                                            title: 'Coversion Profile',
                                            type: 'string',
                                            enum: ['U.S. Web Coated (SWOP)'],
                                            default: 'U.S. Web Coated (SWOP)',
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        documentType: { enum: ['pptx'] },
                        pptxPageSize: {
                            type: 'string',
                            title: 'Page Size',
                            enum: ['letter', 'A4', 'Custom'],
                            default: 'Custom',
                        },
                        pptxPageOrientation: {
                            type: 'string',
                            title: 'Orientation',
                            enum: ['portrait', 'landscape'],
                            default: 'landscape',
                        },
                        pptxPageMargin: {
                            type: 'object',
                            title: 'Page Margin',
                            properties: {
                                top: {
                                    title: 'Top',
                                    type: 'number',
                                    default: 0.4,
                                },
                                left: {
                                    title: 'Left',
                                    type: 'number',
                                    default: 0.4,
                                },
                                bottom: {
                                    title: 'Bottom',
                                    type: 'number',
                                    default: 0.4,
                                },
                                right: {
                                    title: 'Right',
                                    type: 'number',
                                    default: 0.4,
                                },
                            },
                        },
                        pptxPageBleed: {
                            type: 'object',
                            title: 'Page Bleed',
                            properties: {
                                top: {
                                    title: 'Top',
                                    type: 'number',
                                    default: 0,
                                },
                                left: {
                                    title: 'Left',
                                    type: 'number',
                                    default: 0,
                                },
                                bottom: {
                                    title: 'Bottom',
                                    type: 'number',
                                    default: 0,
                                },
                                right: {
                                    title: 'Right',
                                    type: 'number',
                                    default: 0,
                                },
                            },
                        },
                        pptxIsRunningHeader: {
                            type: 'boolean',
                            title: 'Is Running Header',
                            default: false,
                        },
                        pptxIsRunningFooter: {
                            type: 'boolean',
                            title: 'Is Running Footer',
                            default: false,
                        },
                    },
                    dependencies: {
                        pptxPageSize: {
                            oneOf: [
                                {
                                    properties: {
                                        pptxPageSize: { enum: ['Custom'] },
                                        pptxPageWidth: {
                                            title: 'Width',
                                            type: 'number',
                                            default: 13.33,
                                        },
                                        pptxPageHeight: {
                                            title: 'Height',
                                            type: 'number',
                                            default: 7.5,
                                        },
                                    },
                                },
                            ],
                        },
                    },
                },
            ],
        },
        isWithinIFrame: {
            oneOf: [
                {
                    properties: {
                        isWithinIFrame: { enum: [true] },
                        iframeStickyHeaderType: {
                            type: 'string',
                            title: 'Type of sticky header',
                            enum: ['Wrapper', 'Individual elements'],
                            description:
                                'Select if sticky header is inside wrapper or not (please note: Individual elements must have unique class)',
                        },
                    },
                    dependencies: {
                        iframeStickyHeaderType: {
                            oneOf: [
                                {
                                    properties: {
                                        iframeStickyHeaderType: { enum: ['Wrapper'] },
                                        stickyHeaderWrapperId: {
                                            type: 'string',
                                            title: 'Sticky header wrapper ID',
                                            default: '',
                                            description:
                                                'Enter unique ID assigned to sticky wrapper',
                                        },
                                    },
                                    required: ['stickyHeaderWrapperId'],
                                },
                                {
                                    properties: {
                                        iframeStickyHeaderType: { enum: ['Individual elements'] },
                                        stickyHeaderClass: {
                                            type: 'string',
                                            title: 'Sticky header class',
                                            default: '',
                                            description:
                                                'Enter unique class assigned to all individual sticky elements',
                                        },
                                    },
                                    required: ['stickyHeaderClass'],
                                },
                            ],
                        },
                    },
                },
                {
                    properties: {
                        isWithinIFrame: { enum: [false] },
                    },
                },
            ],
        },
        addScripts: {
            oneOf: [
                {
                    properties: {
                        addScripts: { enum: [true] },
                        scriptsHead: {
                            type: 'array',
                            title: 'Enter scripts in the head',
                            items: {
                                type: 'string',
                            },
                        },
                        scriptsBody: {
                            type: 'array',
                            title: 'Enter scripts in the body',
                            items: {
                                type: 'string',
                            },
                        },
                    },
                },
                {
                    properties: {
                        addScripts: { enum: [false] },
                    },
                },
            ],
        },
        isDynamicTheme: {
            oneOf: [
                {
                    properties: {
                        isDynamicTheme: { enum: [true] },
                        functionToSelectTheme: {
                            type: 'string',
                            description: 'Function to select theme',
                            default: `return (data) => { const theme = 'Default'; return theme; }`,
                        },
                    },
                },
            ],
        },
        profileIdTransform: {
            oneOf: [
                {
                    properties: {
                        profileIdTransform: { enum: [true] },
                        functionToProfileIdTransform: {
                            type: 'string',
                            description: 'Function to transform profileId',
                            default: 'return (profileId) => { return profileId }',
                        },
                    },
                },
            ],
        },
        isTransformData: {
            oneOf: [
                {
                    properties: {
                        isTransformData: { enum: [true] },
                        functionToTransform: {
                            type: 'string',
                            description: 'Function to transform',
                            default: 'return (data) => { return data }',
                        },
                    },
                },
            ],
        },
        usePageAnalytics: {
            oneOf: [
                {
                    properties: {
                        usePageAnalytics: { enum: [true] },
                        dynamicEvents: {
                            type: 'array',
                            title: 'Add Event with Custom Configuration',
                            items: {
                                type: 'object',
                                properties: {
                                    eventName: {
                                        type: 'string',
                                        title: 'Event Name',
                                    },
                                    analyticItems: {
                                        type: 'array',
                                        title: 'Page Analytics Items',
                                        items: {
                                            type: 'object',
                                            properties: {
                                                name: {
                                                    type: 'string',
                                                    title: 'Name',
                                                },
                                                value: {
                                                    type: 'string',
                                                    title: 'Value',
                                                },
                                            },
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            ],
        },
        ...customSchemas.layoutSchema.LayoutSchema.dependencies,
    },
};

// Grouping widget properties
const groups = [
    {
        'Data Resources': [
            'dataSource',
            'dataType',
            'includeAllProfiles',
            'includeHistoricalHoldings',
            'includeUnderlyingFundPerformance',
            'includeFundSeriesPerformance',
            'includeAnnotations',
            'isWithinIFrame',
            'iframeStickyHeaderType',
            'stickyHeaderWrapperId',
            'stickyHeaderClass',
            'addScripts',
            'scriptsHead',
            'scriptsBody',
            'documentType',
            'pdfPageSize',
            'pdfPageMargin',
            'pageTitleEn',
            'pageTitleFr',
            'loadingDataText',
            'redirect301',
            'redirect302',
            'selectedFunds',
            'selectFunds',
            'showData',
        ],
        'Style Editor': ['styles', 'themes', 'colorSpace', 'fonts'],
        Theme: ['theme', 'isDynamicTheme', 'functionToSelectTheme'],
        ...customSchemas.textSchema.TextSchema.group,
        ...customSchemas.styleSchema.StyleSchema.group,
        ...customSchemas.layoutSchema.LayoutSchema.group,
        Annotations: [
            'sortAnnotationsBy',
            'annotationsMarkerHtml',
            'annotationsTextHtml',
            'annotationsTextGroupHtml',
        ],
        Resources: ['locales', 'resourceStrings'],
        'PDF Options': [
            'pdfPageSize',
            'pdfPageMargin',
            'pageSize',
            'pageWidth',
            'pageHeight',
            'pageOrientation',
            'pageMargin',
            'pageBleed',
            'addPdfTags',
            'conformance',
            'customConformance',
            'conversionEnabled',
            'coversionProfile',
            'isRunningHeader',
            'isRunningFooter',
        ],
        'PPTX Options': [
            'pptxPageSize',
            'pptxPageOrientation',
            'pptxPageWidth',
            'pptxPageHeight',
            'pptxPageMargin',
            'pptxPageBleed',
            'pptxIsRunningHeader',
            'pptxIsRunningFooter',
        ],
        'Meta Data': [
            'pageTitle',
            'pageDescription',
            'pageAuthor',
            'pageSubject',
            'pageCreator',
            'keywords',
            'canonicalURL',
            'externalCSSLink',
            'robots',
            'fileName',
        ],
        'Cache Control': ['cachingHeader'],
        'Content Security Policy': ['cspHeader'],
        'Transform Data': [
            'profileIdTransform',
            'functionToProfileIdTransform',
            'isTransformData',
            'functionToTransform',
        ],
        'Page Analytics': ['usePageAnalytics', 'dynamicEvents'],
        Accessibility: ['disableTabStaticData'],
    },
];

const RootDocument: commonTypes.IWidget = {
    schema,
    uiSchema: {
        'ui:groups': groups,
        // 'ui:ObjectFieldTemplate': (props: any) => <Grouped.ObjectFieldTemplate {...props} />,
        ...customSchemas.textSchema.TextSchema.uiSchema,
        ...customSchemas.styleSchema.StyleSchema.uiSchema,
        ...customSchemas.layoutSchema.LayoutSchema.uiSchema,
        dataSource: { 'ui:widget': 'CustomTextInput' },
        dataType: { 'ui:widget': 'CustomSelect' },
        // includeAllProfiles: { 'ui:widget': 'CustomCheckbox' },
        // includeHistoricalHoldings: { 'ui:widget': 'CustomCheckbox' },
        // includeUnderlyingFundPerformance: { 'ui:widget': 'CustomCheckbox' },
        // includeFundSeriesPerformance: { 'ui:widget': 'CustomCheckbox' },
        // includeAnnotations: { 'ui:widget': 'CustomCheckbox' },
        annotationFilterProperty: { 'ui:widget': 'CustomTextInput' },
        isWithinIFrame: { 'ui:widget': 'CustomCheckbox' },
        iframeStickyHeaderType: { 'ui:widget': 'CustomSelect' },
        stickyHeaderWrapperId: { 'ui:widget': 'CustomTextInput' },
        stickyHeaderClass: { 'ui:widget': 'CustomTextInput' },
        addScripts: { 'ui:widget': 'CustomCheckbox' },
        scriptsHead: { items: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' } },
        scriptsBody: { items: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' } },
        documentType: { 'ui:widget': 'CustomSelect' },
        pdfPageSize: { 'ui:widget': 'CustomTextInput' },
        pdfPageMargin: { 'ui:widget': 'CustomTextInput' },
        pageTitleEn: { 'ui:widget': 'CustomTextInput' },
        pageTitleFr: { 'ui:widget': 'CustomTextInput' },
        loadingDataText: { 'ui:widget': 'CustomTextInput' },
        colorSpace: { 'ui:widget': 'CustomSelect' },
        styles: {
            'ui:widget': 'CSSEditor',
        },
        theme: { 'ui:widget': 'ThemeSelect' },
        themes: {
            items: {
                'ui:field': 'CollapsibleFieldTemplate',
                themeStyles: {
                    'ui:widget': 'CSSEditor',
                },
                id: { 'ui:widget': 'CustomTextInput' },
                conditionFunction: { 'ui:widget': 'CustomTextInput' },
                colorPallete: {
                    items: {
                        'ui:widget': 'CustomTextInput',
                    },
                },
            },
        },
        fonts: {
            items: {
                'ui:field': 'CollapsibleFieldTemplate',
                name: { 'ui:widget': 'CustomTextInput' },
                url: { 'ui:widget': 'CustomTextInput' },
                format: { 'ui:widget': 'CustomTextInput' },
            },
        },
        locales: {
            items: { 'ui:widget': 'CustomTextInput' },
        },
        sortAnnotationsBy: { 'ui:widget': 'CustomTextInput' },
        annotationsMarkerHtml: { 'ui:widget': 'CustomTextInput' },
        annotationsTextHtml: { 'ui:widget': 'CustomTextInput' },
        annotationsTextGroupHtml: { 'ui:widget': 'CustomTextInput' },
        cachingHeader: { 'ui:widget': 'CustomTextInput' },
        cspHeader: { 'ui:widget': 'CustomTextInput' },
        pageOrientation: { 'ui:widget': 'Orientation' },
        pageMargin: { 'ui:field': 'PdfMarginField' },
        pageWidth: { 'ui:widget': 'InchesInput' },
        pageHeight: { 'ui:widget': 'InchesInput' },
        pageSize: { 'ui:widget': 'CustomSelect' },
        pageBleed: { 'ui:field': 'PdfMarginField' },
        addPdfTags: { 'ui:widget': 'CustomCheckbox' },
        conformance: { 'ui:widget': 'CustomSelect' },
        customConformance: { 'ui:widget': 'CustomTextInput' },
        conversionEnabled: { 'ui:widget': 'CustomCheckbox' },
        coversionProfile: { 'ui:widget': 'CustomSelect' },
        pageTitle: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        pageDescription: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        pageAuthor: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        pageSubject: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        pageCreator: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        keywords: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        canonicalURL: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        externalCSSLink: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '' },
        robots: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '' },
        fileName: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },

        redirect301: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },
        redirect302: { 'ui:widget': 'CustomTextInput', 'ui:placeholder': '{{ }}' },

        functionToSelectTheme: { 'ui:widget': 'JSEditor' },
        isDynamicTheme: { 'ui:widget': 'CustomCheckbox' },
        showData: { 'ui:widget': 'ShowDataButton' },
        selectFunds: { 'ui:widget': 'SelectFundsButton' },
        selectedFunds: { 'ui:widget': 'SelectedFundsDropdown' },
        pptxPageSize: { 'ui:widget': 'CustomSelect' },
        pptxPageOrientation: { 'ui:widget': 'Orientation' },
        pptxPageMargin: { 'ui:field': 'PdfMarginField' },
        pptxPageBleed: { 'ui:field': 'PdfMarginField' },
        pptxPageWidth: { 'ui:widget': 'InchesInput' },
        pptxPageHeight: { 'ui:widget': 'InchesInput' },
        isTransformData: { 'ui:widget': 'CustomCheckbox' },
        functionToTransform: { 'ui:widget': 'JSEditor' },
        profileIdTransform: { 'ui:widget': 'CustomCheckbox' },
        functionToProfileIdTransform: { 'ui:widget': 'JSEditor' },
        usePageAnalytics: { 'ui:widget': 'CustomCheckbox' },
        disableTabStaticData: { 'ui:widget': 'CustomCheckbox' },
        dynamicEvents: {
            items: {
                eventName: { 'ui:widget': 'CustomTextInput' },
                analyticItems: {
                    items: {
                        name: { 'ui:widget': 'CustomTextInput' },
                        value: { 'ui:widget': 'ResourcePicker' },
                    },
                },
            },
        },
        isRunningHeader: { 'ui:widget': 'CustomCheckbox' },
        isRunningFooter: { 'ui:widget': 'CustomCheckbox' },
        pptxIsRunningHeader: { 'ui:widget': 'CustomCheckbox' },
        pptxIsRunningFooter: { 'ui:widget': 'CustomCheckbox' },
    },
    templateView,
    id: 'document',
    contentPlaceHolders: ['children'],
    initialData: (_context?: Record<string, unknown>) => ({}),
};

export default RootDocument;
